import { useI18n } from 'vue-i18n'

export const calculateProductTotalAvailableAmount = accountsList => {
	return accountsList.reduce((acc, account) => {
		if (account.availableBalance) acc += (account.convertedAvailableBalance || 0)
		return acc
	}, null)
}

export const calculateProductTotalAmount = accountsList => {
	return accountsList.reduce((acc, account) => acc + (account.convertedBalance || 0), 0)
}

export const calculateProductTotalAvailableAmountByCurrency = accountsList => {
	return accountsList.reduce((acc, account) => {
		if (account.availableBalance) acc += (account.availableBalance || 0)
		return acc
	}, null)
}

export const calculateProductTotalAmountByCurrency = accountsList => {
	return accountsList.reduce((acc, account) => acc + (account.balance || 0), 0)
}

/*
* Used to created the percentage for each family
* @params - accountFamilyList: Array
*			totalAmount: Int
*/
export const calculateActivePassivePercentage = (accountFamilyList, totalAmount) => {
	return accountFamilyList.map(family => {
		return {
			...family,
			percentage: totalAmount ? ((family.totalAmount * 100) / totalAmount) : 0,
			parsedPercentage: totalAmount ? (Math.round(((family.totalAmount * 100) / totalAmount) * 100) / 100) : 0
		}
	})
}

/*
* Parser to build array with the correct format for chart
* @params - accountFamilies: Array
*/
export const createActivePassiveGraphArray = graphArray => {
	return graphArray.reduce((acc, obj) => {
		if (obj.percentage !== 0) acc.push([obj.description, parseFloat(obj.percentage), obj.description])
		return acc
	}, [])
}

/*
* Used to created the percentage for each product
* @params - productsList: Array
*			totalAmount: Int
*/
export const calculateProductPercentage = (productsList, totalAmount) => {
	return productsList.map(product => {
		return {
			...product,
			percentage: totalAmount ? ((product.totalAmount * 100) / totalAmount) : 0,
			parsedPercentage: totalAmount ? (Math.round(((product.totalAmount * 100) / totalAmount) * 100) / 100) : 0
		}
	})
}

/*
* Parser to build array with the correct format for chart
* @params - products: Array
*/
export const createProductGraphArray = (graphArray) => {
	return graphArray.reduce((acc, obj) => {
		if (obj.percentage !== 0) acc.push([obj.description, parseFloat(obj.percentage), obj.description])
		return acc
	}, [])
}

/*
* Used to created the percentage for each account
* @params - accountsList: Array
*			totalAmount: Int
*/
export const calculateArrayAccountsPercentage = (accountsList, totalAmount) => {
	return accountsList.map(account => {
		return {
			...account,
			percentage: totalAmount ? (((account.convertedBalance || 0) * 100) / totalAmount) : 0,
			parsedPercentage: totalAmount ? (Math.round((((account.convertedBalance || 0) * 100) / totalAmount) * 100) / 100) : 0
		}
	})
}

/*
* Parser to build array with the correct format for chart
* @params - accounts: Array
*/
export const createAccountGraphArray = (graphArray) => {
	const { t } = useI18n()

	const accountsVisible = graphArray.length > 20 ? [] : graphArray
	const cluster = []
	let clusterPercentage = 0

	if (graphArray.length > 20) {
		graphArray.forEach(account => {
			if (account.parsedPercentage <= 1) {
				cluster.push(account)
				clusterPercentage += account.percentage
			} else {
				accountsVisible.push(account)
			}
		})
	}

	const graph = accountsVisible.reduce((acc, obj) => {
		let label = obj.accountDescription ? obj.accountDescription : obj.accountNumber
		if (!obj.accountDescription) {
			if (obj.accountType !== 'DWALLET') {
				label = label.replace(/^(\d{9})(\d{2})(\d{3})*/, '$1 $2 $3')
			} else {
				label = `${label.substr(0, 9)} ${label.substr(9, 2)} ${label.substr(11)}`
			}
		}

		if (obj.percentage !== 0) {
			acc.push([label, parseFloat(obj.percentage), obj.accountNumber])
		}
		return acc
	}, [])

	if (cluster.length) {
		graph.push([`+ ${cluster.length} ${t('accounts.patrimony.labels.accounts')}`, parseFloat(clusterPercentage), 'clusterAccounts'])
	}

	return { graph: graph, cluster: cluster } // we need to also return the cluster for display info
}

/*
* Used to created the percentage of an account inside a table, when we only have the individual account obj
* @params - accountsAmount: Int
*			productTotalAmount: Int
*/
export const calculateAccountPercentage = (accountAmount, productTotalAmount) => {
	const percentage = productTotalAmount ? ((accountAmount * 100) / productTotalAmount) : 0
	const parsedPercentage = Math.round((percentage * 100) / 100)

	return (parsedPercentage === 0 && percentage !== 0) || (parsedPercentage === 100 && percentage !== 100) ? `≈ ${parsedPercentage}` : parsedPercentage
}

export const convertHexToHSL = (hexColor) => {
	const hex = hexColor.replace(/#/, '')
	const rgb = []

	// first we need to convert our hex to rgb
	for (var i = 0; i < 6; i += 2) {
		rgb.push(parseInt(hex.substr(i, 2), 16))
	}

	// We must divide r,g,b by 255 in order to obtain values between 0 and 1
	const r = rgb[0] / 255
	const g = rgb[1] / 255
	const b = rgb[2] / 255

	// Then we find the minimum and maximum of those values (min and max)
	// as well as the difference between them (delta)
	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const delta = max - min
	let h = 0
	let s = 0
	let l = 0

	// Calculate hue
	if (delta === 0) { // No difference
		h = 0
	} else if (max === r) { // Red is max
		h = ((g - b) / delta) % 6
	} else if (max === g) { // Green is max
		h = (b - r) / delta + 2
	} else { // Blue is max
		h = (r - g) / delta + 4
	}

	h = Math.round(h * 60)

	// Make negative hues positive behind 360°
	if (h < 0) h += 360

	// Calculate lightness
	l = (max + min) / 2

	// Calculate saturation
	s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

	// Multiply l and s by 100
	s = +(s * 100).toFixed(1)
	l = +(l * 100).toFixed(1)

	// `hsl(${h}, ${s}%, ${l}%)`
	// we are sending this as an object, so it has the right structure to create an array of colors based in this content
	return { h: h, s: s, l: l }
}

export const createColorBasedArray = (baseColor, arrayLength) => {
	const aux = []
	const hueOffset = baseColor.h
	// saturation values
	let sOffset = baseColor.s
	const sIncrement = (90 - sOffset) / arrayLength
	// lightness
    let lOffset = baseColor.l
    const lIncrement = (90 - lOffset) / arrayLength

	// first element will use the basecolor
    aux[0] = `hsl(${hueOffset}, ${sOffset}%, ${lOffset}%)`

	for (var i = 1; i < arrayLength; i++) {
		lOffset += lIncrement
		sOffset -= sIncrement

		aux[i] = `hsl(${hueOffset}, ${sOffset}%, ${lOffset}%)`
	}

	return aux
}
